import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormTagField,
  useParsedTagErrors,
} from '@sib/ui';
import { X } from '@sib/ui/icons';
import { Invitee } from '@spendbrain/types/user';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { InviteesList } from './InviteesList';
import { ShareDialogFormMessage } from './ShareDialogFormMessage';
import { useRemoveAccessDialog } from './use-remove-access-dialog';
import {
  MESSAGE_VISIBILITY_TIMEOUT,
  ShareDialogFormField,
  useFormProps,
  useInviteesList,
  useOnSubmit,
} from './ShareDialog.utils';

interface ShareDialogProps {
  onClose: () => void;
  open?: boolean;
}

export const ShareDialog = ({ onClose, open }: ShareDialogProps) => {
  const { t } = useTranslation();
  const {
    isLoading: isListLoading,
    invitees,
    onInviteesAdd,
    onInviteeRemove,
  } = useInviteesList();

  const formProps = useFormProps({ invitees });
  const {
    formState: {
      errors: { emailAddresses: error },
    },
    getValues,
    reset,
  } = formProps;
  const {
    onSubmit,
    isPending: isSubmitting,
    errorMessage,
    successMessage,
  } = useOnSubmit({
    onSuccess: (invitees: Invitee[]) => {
      onInviteesAdd(invitees);
      reset();
    },
  });

  const errors = useParsedTagErrors(error);

  const formErrorMessage = errors[0];
  const emails = getValues(ShareDialogFormField.EmailAddresses);

  const isSubmitDisabled =
    errors.some(error => error.variant === 'error') || emails.length === 0;

  const submitWithWarnings = useCallback(() => {
    if (isSubmitDisabled) return;
    onSubmit({
      emailAddresses: getValues()[ShareDialogFormField.EmailAddresses],
    });
  }, [onSubmit, getValues, isSubmitDisabled]);

  const onModalClose = () => {
    onClose();
    reset();
  };

  const removeAccessDialog = useRemoveAccessDialog({
    onRemove: onInviteeRemove,
  });

  return (
    <>
      <ConfirmationDialog
        onClose={removeAccessDialog.onCancel}
        onConfirm={removeAccessDialog.onConfirm}
        open={removeAccessDialog.isOpen}
        loading={removeAccessDialog.isLoading}
        ConfirmButtonProps={{ label: t('components.shareDialog.remove') }}
      >
        <div className="flex flex-col items-center gap-7">
          <h3 className="text-primary text-xl leading-7 font-semibold">
            {t('components.shareDialog.removeAccess.confirmation.title')}
          </h3>
          <p className="text-primary text-sm">
            {t('components.shareDialog.removeAccess.confirmation.description', {
              name: removeAccessDialog.inviteeToRemove?.name,
            })}
          </p>
        </div>
      </ConfirmationDialog>
      {!removeAccessDialog.isOpen && (
        <Dialog
          open={open}
          onOpenChange={() => {
            if (!open) return;
            onModalClose();
          }}
        >
          <DialogContent className="max-h-[95vh] max-w-[98vw] overflow-y-auto px-2 py-7 sm:max-w-[70vw] sm:px-[50px] md:min-w-[1000px]">
            <DialogHeader>
              <Button
                onClick={onModalClose}
                size="icon"
                variant="ghost"
                className="text-primary absolute top-4 right-2 sm:top-7 sm:right-10"
              >
                <X className="text-primary" />
              </Button>
              <DialogTitle className="text-primary text-center text-[38px] leading-[38px] font-semibold sm:leading-[28px]">
                {t('components.shareDialog.title')}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground pt-4 text-center text-lg leading-[28px] font-semibold sm:text-xl">
                {t('components.shareDialog.subtitle')}
              </DialogDescription>
              <div className="py-10">
                <div className="h-5 w-full">
                  {errorMessage || successMessage ? (
                    <ShareDialogFormMessage
                      message={errorMessage || successMessage || ''}
                      variant={errorMessage ? 'error' : 'success'}
                      timeout={MESSAGE_VISIBILITY_TIMEOUT}
                    />
                  ) : (
                    formErrorMessage && (
                      <ShareDialogFormMessage
                        message={formErrorMessage?.message || ''}
                        variant={
                          formErrorMessage?.variant === 'error'
                            ? 'error'
                            : 'warning'
                        }
                      />
                    )
                  )}
                </div>
              </div>
              <div className="shadow-[0px_4px_6px_0px_rgba(0,0,0,0.09) flex flex-col items-start gap-2 rounded-sm border-1 p-3.5">
                <p className="text-primary text-sm leading-4.5 font-semibold">
                  {t('components.shareDialog.invite')}
                </p>
                <div className="w-full">
                  <Form onSubmit={onSubmit} {...formProps}>
                    <div className="border-border flex w-full items-start gap-2 sm:gap-4">
                      <div className="flex-[1_0]">
                        <FormTagField
                          placeholder={t(
                            'components.shareDialog.fieldPlaceholder'
                          )}
                          autoComplete="off"
                          name={ShareDialogFormField.EmailAddresses}
                        />
                      </div>
                      <Button
                        className="h-10 px-1 sm:px-4"
                        type="button"
                        loading={isSubmitting}
                        onClick={submitWithWarnings}
                        disabled={isSubmitDisabled || isListLoading}
                      >
                        {t('components.shareDialog.invite')}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            </DialogHeader>
            <InviteesList
              onRemove={removeAccessDialog.onRemoveClick}
              data={invitees}
              loading={isListLoading}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
